/**
 * @fileoverview Main POI data management for CasebookMap.
 * Loads, indexes, and provides access to place/people data from JSON sources.
 * @module data/PoiDataManager
 */

import { fixRelativeChildPathWithParentFilePath, shuffledCopy, appendCacheBuster, compareTwoStringsDice } from '../utils/jrfuncs.js';
import { formatAddressForItemNicely, formatAddressWithApt, sortDataByField, sortDataByFieldAddressSpecial } from '../utils/nynfuncs.js';
import { loadGeoJsonRowDataFromPath } from '../utils/jrgeofuncs.js';
import { DEBUG_CBM, DEBUG_SIMULATE_NETWORK_DELAY_MS, SEARCH_CONSTANTS, MAP_MODES, RASTER_POI_COORDS } from '../constants.js';


/**
 * Manages loading and storage of data sources.
 * Loads multiple JSON data sources (places, people) and combines them into a searchable index.
 * Search functionality is provided by DbSearcher static methods.
 * @class
 */
export class PoiDataManager {
    /**
     * Creates a new PoiDataManager instance.
     * @param {Object} mapApp - The MapApp instance
     * @param {string} basePath - Base path for resolving relative data file URLs
     * @param {Array<Object>} dataSourcesConfig - Array of data source configurations
     * @param {string} dataSourcesConfig[].id - Unique identifier for the data source (e.g., 'places', 'people')
     * @param {string} dataSourcesConfig[].url - URL to fetch the JSON data from
     */
    constructor(mapApp, basePath, dataSourcesConfig) {
        this.mapApp = mapApp;
        //
        this.basePath = basePath;
        this.config = dataSourcesConfig || [];
        this.data = {}; // Dynamic object to hold data by source id
        this.dataCombined = []; // All data combined for searching
        this.lookupIndexes = {}; // field -> Map(value -> first item), see findItemByFieldUsingIndex
        this.loaded = false;
        this.loading = false;

        // Define which columns to keep when combining data
        this.keepColumnList = [
            'dName',
            'lastName',
            'lead',
            'address',
            'apt',
            'jregion',
            'loclabel',
            'ptype',
            'pcat',
            'pcatg',
            'locid',
            'offmap',
            'gender',
            'addrFull',
            'htype',
            'hint',
            'geometry',
            'listype',
            'source',

            'uid',
            'sort_a',
            'sort_n',
            'jregionloc'
        ];
    }


    // ==========================================
    // LOGGING HELPERS
    // ==========================================

    /**
     * Adds an info-level log message via MapApp.
     * @param {string} message - The log message text
     */
    addMessage(message) {
        this.mapApp.addMessage(message);
    }

    /**
     * Adds a warning-level log message via MapApp.
     * @param {string} message - The warning message text
     */
    addWarning(message) {
        this.mapApp.addWarning(message);
    }

    /**
     * Adds an error-level log message via MapApp.
     * @param {string} message - The error message text
     */
    addError(message) {
        console.error(message);
        this.mapApp.addError(message);
    }


    // ==========================================
    // INIT AND LOAD
    // ==========================================

    /**
     * Loads all configured data sources in parallel.
     * Combines loaded data into a single searchable array with computed fields.
     * @async
     * @returns {Promise<boolean>} True if all sources loaded successfully
     * @throws {Error} If a critical loading error occurs
     */
    async loadAll() {
        if (this.loading) {
            return false;
        }
        if (this.loaded) {
            return true;
        }

        this.loading = true;

        try {
            const loadPromises = [];

            // Load each configured data source
            for (const sourceConfig of this.config) {
                if (!sourceConfig.id) {
                    this.addError(`Data source missing id, skipping.`);
                    continue;
                }

                if (!sourceConfig.url) {
                    this.addError(`Data source '${sourceConfig.id}' missing url, skipping.`);
                    continue;
                }

                // Initialize data array for this source
                this.data[sourceConfig.id] = [];

                // Add load promise
                loadPromises.push(
                    this.loadDataSource(sourceConfig.id, sourceConfig.url, sourceConfig.projection)
                        .then(data => {

                        // Process each item to filter columns and add computed fields
                        for (const item of data) {
                            const processedItem = this.processDbItemForCombined(item);
                            if (processedItem.jhidden) {
                                // skip these
                                continue;
                            }
                            this.dataCombined.push(processedItem);
                        }

                        })
                        .catch(error => {
                            this.addError(`Failed to load data source "${sourceConfig.id}" from "${sourceConfig.url}": ${error}.`);
                            // Continue loading other sources even if one fails
                        })
                );
            }

            // Wait for all data sources to load
            await Promise.all(loadPromises);

            // Transform POI coordinates for raster mode if needed
            this.applyRasterCoordTransform(this.dataCombined);

            // Precompute sorting fields, and hierarchy, etc.
            this.preCompueSortingHierarchy(this.dataCombined);

            // set flags to say loaded
            this.loaded = true;
            this.loading = false;

            return true;

        } catch (error) {
            const errorMessage = `Failed to load data sources: ${error}.`;
            this.addError(errorMessage);
            this.loading = false;
            throw new Error(errorMessage);
        }
    }



    /**
     * Transforms POI coordinates for raster mode maps.
     * Converts from the source coordinate system (normalized or pixel with top-left origin)
     * to the internal raster pixel system (x: 0..imageWidth, y: -imageHeight..0).
     * Only applies when mapMode is raster and rasterConfig.poiCoords is set.
     * @param {Array<Object>} data - Array of POI items to transform in place
     * @private
     */
    applyRasterCoordTransform(data) {
        const worldConfig = this.mapApp.worldConfig;
        if (!worldConfig || worldConfig.mapMode !== MAP_MODES.RASTER) {
            return;
        }
        const rasterConfig = worldConfig.rasterConfig;
        if (!rasterConfig || !rasterConfig.poiCoords) {
            return;
        }

        const poiCoords = rasterConfig.poiCoords;
        const imageWidth = rasterConfig.imageWidth;
        const imageHeight = rasterConfig.imageHeight;

        for (const item of data) {
            if (!item.geometry || !item.geometry.coordinates) {
                continue;
            }
            const coords = item.geometry.coordinates;
            let x = coords[0];
            let y = coords[1];

            if (poiCoords === RASTER_POI_COORDS.NORMALIZED) {
                // Convert from normalized (0,0 top-left .. 1,1 bottom-right) to pixel coords
                x = x * imageWidth;
                y = y * imageHeight;
            }

            // Convert from top-left origin (0,0 .. imageWidth,imageHeight)
            // to internal raster system (0,-imageHeight .. imageWidth,0)
            coords[0] = x;
            coords[1] = -y;
        }
    }


    /**
     * Loads a single data source from a URL.
     * Handles both GeoJSON FeatureCollection format and plain JSON arrays.
     * @async
     * @param {string} sourceName - Identifier for the data source (e.g., 'places', 'people')
     * @param {string} url - URL to fetch the JSON data from
     * @returns {Promise<Array<Object>>} Array of data items with _sourceName added
     * @throws {Error} If fetch fails or data format is invalid
     * @private
     */
    async loadDataSource(sourceName, url, projection) {
        url = fixRelativeChildPathWithParentFilePath(this.basePath, url);
        url = appendCacheBuster(url, this.mapApp.getWorldCacheBusterVersion());
        if (DEBUG_CBM) {
            console.log(`DEBUG: Loading POI data from: ${url}.`);
        }
        let data = await loadGeoJsonRowDataFromPath(url, `load data source "${sourceName}"`, projection, sourceName, DEBUG_SIMULATE_NETWORK_DELAY_MS)
        return data;
    }


    // ==========================================
    // ACCESSORS ETC.
    // ==========================================

    /**
     * Checks if data has been loaded and is ready for searching.
     * Logs a warning if data is not yet loaded.
     * @returns {boolean} True if data is loaded and ready
     * @private
     */
    checkDataReady() {
        if (!this.loaded) {
            this.addError('PoiDataManager not loaded yet.');
            return false
        }
        return true;
    }

    /**
     * Check if data is loaded
     * @returns {boolean} True if data is loaded
     */
    isLoaded() {
        return this.loaded;
    }

    /**
     * Get loading status
     * @returns {boolean} True if currently loading
     */
    isLoading() {
        return this.loading;
    }

    /**
     * Gets all combined data for direct access.
     * @returns {Array<Object>} Combined array of all data items, or empty array if not loaded
     */
    getDataCombined() {
        if (!this.loaded) {
            this.addError('PoiDataManager not loaded yet.');
            return [];
        }
        return this.dataCombined;
    }

    /**
     * Gets data source for searching, with data readiness check.
     * Handles random shuffle option if specified.
     * @param {Object} options - Search options
     * @param {boolean} [options.random] - If true, returns shuffled copy
     * @returns {Array|null} Data array to search, or null if not ready
     */
    getDataSourceForSearch(searchPack) {
        if (!this.checkDataReady()) {
            return null;
        }
        if (searchPack.random) {
            return shuffledCopy(this.dataCombined);
        }
        return this.dataCombined;
    }



    // ==========================================
    // HELPERS
    // ==========================================

    /**
     * Gets an item by its lead field.
     * @param {string} lead - The lead value to search for
     * @returns {Object|null} The item with matching lead, or null if not found
     */
    findItemByLead(lead) {
        return this.findItemByFieldUsingIndex('lead', lead);
    }

    /**
     * Gets an item by its unique identifier.
     * @param {string} uid - The unique identifier to search for
     * @returns {Object|null} The item with matching uid, or null if not found
     */
    findItemByUid(uid) {
        return this.findItemByFieldUsingIndex('uid', uid);
    }

    /**
     * Finds the FIRST item in dataCombined whose field equals value (same answer as a linear
     * dataCombined.find), through a Map index built on first use. These lookups run per stored
     * bookmark/history stub on every world load, per PDF bookmark on every page render and per
     * import line, so a linear scan over the whole POI list each time added up.
     * The index is only cached once the data is loaded, and is dropped whenever dataCombined changes.
     * @param {string} field - 'uid' or 'lead'
     * @param {*} value - The value to look up
     * @returns {Object|null} The first matching item, or null
     * @private
     */
    findItemByFieldUsingIndex(field, value) {
        if (!this.loaded) {
            // still loading: no index yet (it would be built from a partial list)
            return this.dataCombined.find(item => item[field] === value) || null;
        }
        let index = this.lookupIndexes[field];
        if (!index) {
            index = new Map();
            for (const item of this.dataCombined) {
                // keep the FIRST item for a value, like Array.find
                if (!index.has(item[field])) {
                    index.set(item[field], item);
                }
            }
            this.lookupIndexes[field] = index;
        }
        return index.get(value) || null;
    }

    /**
     * Drops the lookup indexes (call whenever dataCombined is replaced or reordered).
     * @private
     */
    invalidateLookupIndexes() {
        this.lookupIndexes = {};
    }

    /**
     * Clears all loaded data and resets our state.
     */
    clear() {
        this.data = {};
        this.dataCombined = [];
        this.invalidateLookupIndexes();
        this.loaded = false;
        this.loading = false;
    }




    // ==========================================
    // MAIN FUNCS FOR PROCESSING MAIN DBS, combinging into one canonical DB
    // And doing any preprocessing like dropping unnneded fields, projecting, etc.
    // ==========================================





    /**
     * Processes an item for inclusion in the combined dataset.
     * Extracts only the columns specified in keepColumnList and adds computed fields
     * (dNameLower, addressLower) for efficient case-insensitive searching?
     * @param {Object} item - Original item from a data source (may be GeoJSON feature or plain object)
     * @returns {Object} Processed item with filtered columns and computed lowercase fields
     * @private
     */
    processDbItemForCombined(item) {
        const processedItem = {};

        // Handle GeoJSON features vs plain objects
        const properties = item.properties || item._properties || item;

        // Copy only the columns in keepColumnList
        for (const column of this.keepColumnList) {
            // Check in properties first, then in the item itself
            if (properties.hasOwnProperty(column)) {
                processedItem[column] = properties[column];
            } else if (item.hasOwnProperty(column)) {
                processedItem[column] = item[column];
            }
        }

        // Add computed columns

        // assign unique uid - note that we assume this will make a unique id; TODO: check and delete duplicates
        if (!processedItem.uid) {
            processedItem.uid = processedItem.lead + '_' + processedItem.dName
        }

        // computed full address
        if (false) {
            if (!processedItem.addrFull) {
                processedItem.addrFull = formatAddressWithApt(processedItem.address, processedItem.apt);
            }
        }

        // clear unlisted info
        if (processedItem.listype=='private' && processedItem.jregion != 'XX') {
            // (a listing with no hint of its own would otherwise read "undefined[TRUE ADDRESS IS ...]")
            processedItem.hint = (processedItem.hint || '') + "[TRUE ADDRESS IS " + formatAddressForItemNicely(processedItem) +"]";
            processedItem.address = 'unlisted address';
            processedItem.apt = '';
            processedItem.jregion = 'XX';
            processedItem.loclabel = '000';
            processedItem.locid = -1;
            processedItem.geometry = null;
        }

        // jregionloc as a convenience function for advanced searching
        if (!processedItem.jregionloc) {
            const jregionloc = `${processedItem.jregion}${processedItem.loclabel}`
            processedItem.jregionloc = jregionloc;
        }

        return processedItem;
    }





    /**
     * Precomputes sorting fields and hierarchy information for the combined dataset.
     * Builds sort indices for both name and address fields.
     * @param {Array<Object>} data - The combined data array to process
     * @private
     */
    preCompueSortingHierarchy(data) {
        // ATTN: we will eventually do this in code when creating the json data but for testing now we do it in this code

        //console.log('DEBUG: Starting preCompueSortingHierarchy..');

        // step 1: the name sort index
        this.justBuildSortField(data, 'dName', 'sort_n');

        // step 2: the address sort index
        this.justBuildSortField(data, 'address', 'sort_a');

        //console.log('DEBUG:  Finished preCompueSortingHierarchy.');
    }




    /**
     * Builds a numeric sort index field on each item based on sorted order of a given field.
     * Sorts items by the specified field and assigns sequential indices to the sortFieldName.
     * Skips processing if the sort field is already populated.
     * @param {Array<Object>} data - Array of data items to process
     * @param {string} fieldName - Field name to sort by (e.g., 'dName', 'address')
     * @param {string} sortFieldName - Field name to store the sort index (e.g., 'sort_n', 'sort_a')
     * @private
     */
    justBuildSortField(data, fieldName, sortFieldName) {
        // set the sortFieldName, and set pids of rows to their parent ids with common prefix of the field value
        // NOTE: this will NOT identify places with a COMMON prefix in their name which is how most chain stores work (e.g. "Adam Hats (neighborhood)"), BECAUSE there is no PARENT store names with the base name...
        // how we de want to handle those cases?

        if (data.length<1 || !sortFieldName || data[0][sortFieldName]) {
            //nothing to do
            // console.log(`DEBUG:  Skipping build of sort field ${sortFieldName}.`);
            return;
        }

        // Step 1: Sort data rows by fieldName using natural/alphanumeric sort
        // This ensures "7 Main Street" < "55 Main Street" < "100 Main Street"

        // Step 1: sorted version of data
        let sortedItems;
        if (fieldName == 'address') {
            sortedItems = sortDataByFieldAddressSpecial(data);
        } else {
            // kludge to know whether to trust this field
            sortedItems = sortDataByField(data, fieldName, false);
        }

        // Step 2: Walk the sorted item list, tracking a single potential parent
        // start saved index at 1 so it always evaluated to bool true
        let index = 1;
        for (const item of sortedItems) {
            // remember sorting index
            item[sortFieldName] = index;
            index += 1;
        }
    }






    /**
     * Tests if an item is within a given distance of any item matching a category.
     * Uses a cache to avoid re-filtering the dataset for repeated category lookups.
     * @param {Object} item - The item to test proximity for
     * @param {string} category - Category string to match against pcat/pcatg fields
     * @param {number} dist - Maximum distance in native Mercator units
     * @param {Map<string, Array<Object>>} pcatDataCache - Cache mapping category strings to filtered item arrays
     *   (a Map, since the keys are whatever the user typed)
     * @returns {boolean} True if item is within dist of any matching category item
     */
    isNearPcat(item, category, dist, pcatDataCache) {
        // this is a fancy advanced fuction that returns true if item is near any thing with matching pcat
        // importantly it uses pcatDataCache to cache pcat filtered list for efficiency since this is an expensive operation

        let pcatData = pcatDataCache.get(category);
        if (!pcatData) {
            // we have to fetch it and store it in the cache
            pcatData = this.fetchMatchingPcatData(category);
            pcatDataCache.set(category, pcatData);
        }

        // Get item's coordinates
        const itemGeometry = item.geometry;
        if (!itemGeometry || !itemGeometry.coordinates) {
            return false;
        }
        const [itemX, itemY] = itemGeometry.coordinates;

        // Use squared distance for efficiency (avoid sqrt)
        const distSquared = dist * dist;

        // Check if item is within dist units of ANY row in pcatData
        for (const pcatItem of pcatData) {
            if (!pcatItem.geometry || !pcatItem.geometry.coordinates) {
                continue;
            }
            const [pcatX, pcatY] = pcatItem.geometry.coordinates;
            const dx = pcatX - itemX;
            const dy = pcatY - itemY;
            if (dx * dx + dy * dy <= distSquared) {
                return true;
            }
        }
        return false;
    }


    /**
     * Returns all items whose pcat or pcatg field contains the given category substring.
     * Performs case-insensitive matching.
     * @param {string} category - Category substring to match
     * @returns {Array<Object>} Array of matching data items
     * @private
     */
    fetchMatchingPcatData(category) {
        // Return a list of rows whose pcat or pcatg includes the category (case-insensitive)
        const categoryLower = category.toLowerCase();
        return this.dataCombined.filter(item => {
            const pcat = item.pcat ? item.pcat.toLowerCase() : '';
            const pcatg = item.pcatg ? item.pcatg.toLowerCase() : '';
            return pcat.includes(categoryLower) || pcatg.includes(categoryLower);
        });
    }








    /**
     * Tests if an item is within a given distance of any item in a result list.
     * Uses squared distance comparison for efficiency (avoids sqrt).
     * @param {Object} item - The item to test proximity for
     * @param {number} dist - Maximum distance in native Mercator units
     * @param {Array<Object>} results - Array of result items to check distance against
     * @returns {boolean} True if item is within dist of any result item
     */
    isNearResultList(item, dist, results) {
        // this is a fancy advanced fuction that returns true if item is near any thing with matching pcat
        // importantly it uses pcatDataCache to cache pcat filtered list for efficiency since this is an expensive operation

        // Get item's coordinates
        const itemGeometry = item.geometry;
        if (!itemGeometry || !itemGeometry.coordinates) {
            return false;
        }
        const [itemX, itemY] = itemGeometry.coordinates;

        // Use squared distance for efficiency (avoid sqrt)
        const distSquared = dist * dist;

        // Check if item is within dist units of ANY row in pcatData
        for (const result of results) {
            if (!result.geometry || !result.geometry.coordinates) {
                continue;
            }
            const [resultX, resultY] = result.geometry.coordinates;
            const dx = resultX - itemX;
            const dy = resultY - itemY;
            if (dx * dx + dy * dy <= distSquared) {
                return true;
            }
        }
        return false;
    }


























    /**
     * Evaluates a structured field query against a data item.
     * Supports logical operators (and, or, not), implicit AND for multiple keys,
     * and field operators (contains, startsWith, endsWith, in, not, exists).
     * @param {Object} fquery - Structured query object (see examples in method body)
     * @param {Object} item - Data item to evaluate the query against
     * @param {boolean} flagCaseInsensitive - If true, all string comparisons are case insensitive
     * @returns {boolean} True if the item matches the query
     */
    evaluateFQuery(fquery, item, flagCaseInsensitive) {
        // Evaluate a structured field query against a data item, returning true/false.
        //
        // Logical operators:
        //   { and: [query1, query2, ...] }        - all must match
        //   { or: [query1, query2, ...] }         - at least one must match
        //   { not: query }                        - negates the inner query
        //
        // Multiple keys in one object are implicitly ANDed:
        //   { ptype: "person", htype: "A" }       - equivalent to { and: [{ ptype: "person" }, { htype: "A" }] }
        //
        // Field values can be:
        //   Simple string equality:   { ptype: "person" }
        //   Operator object:
        //     { dName: { contains: "bakery" } }   - substring match
        //     { dName: { startsWith: "A" } }      - prefix match
        //     { dName: { endsWith: "son" } }      - suffix match
        //     { htype: { in: ["A", "D"] } }       - match any value in array
        //     { htype: { not: "A" } }             - not equal
        //     { htype: { exists: true } }         - field is present and non-empty
        //
        // If flagCaseInsensitive is true, all string comparisons are case insensitive.
        // Missing/undefined fields on the item return false (no match) rather than throwing.
        //
        // Examples:
        //   { ptype: "person", htype: { in: ["A", "D"] } }
        //   { and: [{ ptype: "person"}, {or: [{htype: "A"},{htype: "D"}]}] }
        //   { not: { jregion: "GD" } }
        //   { dName: { contains: "bakery" } }

        const keys = Object.keys(fquery);

        // -- Logical operators --

        // { and: [...] }
        if (keys.length === 1 && keys[0] === 'and') {
            const clauses = fquery.and;
            if (!Array.isArray(clauses)) throw new Error('evaluateFQuery: "and" value must be an array');
            return clauses.every(clause => this.evaluateFQuery(clause, item, flagCaseInsensitive));
        }

        // { or: [...] }
        if (keys.length === 1 && keys[0] === 'or') {
            const clauses = fquery.or;
            if (!Array.isArray(clauses)) throw new Error('evaluateFQuery: "or" value must be an array');
            return clauses.some(clause => this.evaluateFQuery(clause, item, flagCaseInsensitive));
        }

        // { not: {...} }
        if (keys.length === 1 && keys[0] === 'not') {
            return !this.evaluateFQuery(fquery.not, item, flagCaseInsensitive);
        }

        // -- Multiple keys: implicit AND --
        if (keys.length > 1) {
            return keys.every(key => this.evaluateFQuery({ [key]: fquery[key] }, item, flagCaseInsensitive));
        }

        // -- Single field key --
        const fieldName = keys[0];
        const condition = fquery[fieldName];
        const rawValue = item[fieldName];

        // Operator object form: { fieldName: { operator: operand } }
        if (condition !== null && typeof condition === 'object' && !Array.isArray(condition)) {
            return this.evaluateFQueryOperator(fieldName, condition, rawValue, flagCaseInsensitive);
        }

        // Simple string equality: { fieldName: "value" }
        if (rawValue === undefined || rawValue === null) return false;
        const itemStr = String(rawValue);
        const condStr = String(condition);
        if (flagCaseInsensitive) {
            return itemStr.toLowerCase() === condStr.toLowerCase();
        }
        return itemStr === condStr;
    }


    /**
     * Evaluates a single field operator object against a raw value.
     * Supports operators: contains, startsWith, endsWith, in, not, exists.
     * @param {string} fieldName - Name of the field being evaluated (for error messages)
     * @param {Object} operatorObj - Operator object with exactly one key (e.g., {contains: "x"})
     * @param {*} rawValue - The actual field value from the data item
     * @param {boolean} flagCaseInsensitive - If true, string comparisons are case insensitive
     * @returns {boolean} True if the value satisfies the operator condition
     * @throws {Error} If operator object has multiple keys or unknown operator
     * @private
     */
    evaluateFQueryOperator(fieldName, operatorObj, rawValue, flagCaseInsensitive) {
        // Evaluate an operator object like { contains: "x" }, { in: [...] }, { exists: true }, etc.
        const ops = Object.keys(operatorObj);
        if (ops.length !== 1) throw new Error(`evaluateFQuery: operator object for "${fieldName}" must have exactly one key, got: ${ops.join(', ')}`);

        const op = ops[0];
        const operand = operatorObj[op];

        switch (op) {
            case 'contains': {
                if (rawValue === undefined || rawValue === null) return false;
                const hay = String(rawValue);
                const needle = String(operand);
                return flagCaseInsensitive
                    ? hay.toLowerCase().includes(needle.toLowerCase())
                    : hay.includes(needle);
            }
            case 'startsWith': {
                if (rawValue === undefined || rawValue === null) return false;
                const hay = String(rawValue);
                const needle = String(operand);
                return flagCaseInsensitive
                    ? hay.toLowerCase().startsWith(needle.toLowerCase())
                    : hay.startsWith(needle);
            }
            case 'endsWith': {
                if (rawValue === undefined || rawValue === null) return false;
                const hay = String(rawValue);
                const needle = String(operand);
                return flagCaseInsensitive
                    ? hay.toLowerCase().endsWith(needle.toLowerCase())
                    : hay.endsWith(needle);
            }
            case 'in': {
                if (!Array.isArray(operand)) throw new Error(`evaluateFQuery: "in" operand for "${fieldName}" must be an array`);
                if (rawValue === undefined || rawValue === null) return false;
                const itemStr = String(rawValue);
                if (flagCaseInsensitive) {
                    const itemLower = itemStr.toLowerCase();
                    return operand.some(v => String(v).toLowerCase() === itemLower);
                }
                return operand.some(v => String(v) === itemStr);
            }
            case 'not': {
                if (rawValue === undefined || rawValue === null) return true;
                const itemStr = String(rawValue);
                const condStr = String(operand);
                if (flagCaseInsensitive) {
                    return itemStr.toLowerCase() !== condStr.toLowerCase();
                }
                return itemStr !== condStr;
            }
            case 'exists': {
                const hasValue = rawValue !== undefined && rawValue !== null && rawValue !== '';
                return operand ? hasValue : !hasValue;
            }
            default:
                throw new Error(`evaluateFQuery: unknown operator "${op}" for field "${fieldName}"`);
        }
    }



    evaluateMultiFieldQuery(fieldList, operatorObj, comboOperator, valueOperator, rawValueList, flagCaseInsensitive) {
        // fieldList is a list of fields inside operatorObj
        // comboOperator is from: all, any, none
        // valueOperator is from "contains", "equals", "notequals"
        // rawValue is a string value
        // flagCaseInsensitive controls whether tests are case insensitive
        // return a boolean value which evaluate whether for comboOperator of the values in rawValueList, ANY fields in fieldList valueOperator each value

        // helper: test whether a single field value matches a single search value using valueOperator
        const testMatch = (fieldVal, searchVal) => {
            if (fieldVal === undefined || fieldVal === null) return false;
            const fStr = flagCaseInsensitive ? String(fieldVal).toLowerCase() : String(fieldVal);
            const sStr = flagCaseInsensitive ? String(searchVal).toLowerCase() : String(searchVal);
            switch (valueOperator) {
                case 'contains':  return fStr.includes(sStr);
                case 'equals':    return fStr === sStr;
                case 'notequals': return fStr !== sStr;
                case 'fuzzy': return this.fuzzyStringMatchTrueFalse(fStr,sStr);
                default:
                    throw new Error(`evaluateMultiFieldQuery: unknown valueOperator "${valueOperator}"`);
            }
        };

        // for a single search value, check if ANY field in fieldList matches
        const valueMatchesAnyField = (searchVal) => {
            return fieldList.some(field => testMatch(operatorObj[field], searchVal));
        };

        switch (comboOperator) {
            case 'all':  return rawValueList.every(val => valueMatchesAnyField(val));
            case 'any':  return rawValueList.some(val => valueMatchesAnyField(val));
            case 'none': return !rawValueList.some(val => valueMatchesAnyField(val));
            default:
                throw new Error(`evaluateMultiFieldQuery: unknown comboOperator "${comboOperator}"`);
        }
    }



    fuzzyStringMatchTrueFalse(hayStack, needle, threshold=SEARCH_CONSTANTS.DEFAULT_FUZZY_MATCH_THRESHOLD) {
        // Dice coefficient of character bigrams (utils/jrfuncs.js; this used the string-similarity
        // library's global, which the map page never loaded)
        const score = compareTwoStringsDice(hayStack, needle);
        return score >= threshold;
    }




}


